import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ACTIVITY_TIME_ZONE, DATE_FORMAT } from '../../activity/utils/activityFormUtils';
import { DriverActivityRiskDailyHit, DriverActivityRiskLogItem, RISK_LOG_CHANGE_TYPE } from '../graphql/queries';

dayjs.extend(utc);
dayjs.extend(timezone);

export const RISK_LOG_PAGE_SIZE = 100;
/** 日期跨度上限（含首尾），与后端 31 天校验一致 */
export const MAX_RANGE_DAYS = 31;
export const EMPTY_MARK = '--';
export const DRIVER_ID_REGEX = /^[1-9]\d*$/;
export const PHONE_REGEX = /^\d{10}$/;

const WEEKDAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

/** 搜索条件，全部以字符串形式放在 URL query 中 */
export interface RiskLogSearchParams {
    driverId?: string;
    phone?: string;
    startDate: string;
    endDate: string;
}

const PARAM_KEYS: (keyof RiskLogSearchParams)[] = ['driverId', 'phone', 'startDate', 'endDate'];

export function toSearchParams(params: RiskLogSearchParams): URLSearchParams {
    const sp = new URLSearchParams();
    PARAM_KEYS.forEach((key) => {
        const value = params[key]?.trim();
        if (value) sp.set(key, value);
    });
    return sp;
}

/** 结果页解析 URL：缺日期或司机ID / 手机号都为空时返回 null */
export function parseSearchParams(sp: URLSearchParams): RiskLogSearchParams | null {
    const get = (key: string) => sp.get(key)?.trim() || undefined;
    const startDate = get('startDate');
    const endDate = get('endDate');
    const driverId = get('driverId');
    const phone = get('phone');
    if (!startDate || !endDate || (!driverId && !phone)) return null;
    return { driverId, phone, startDate, endDate };
}

/** 默认日期：截止美西昨天的近 7 天 */
export function getDefaultDateRange(now: dayjs.Dayjs = dayjs()): [string, string] {
    const yesterday = now.tz(ACTIVITY_TIME_ZONE).subtract(1, 'day');
    return [yesterday.subtract(6, 'day').format(DATE_FORMAT), yesterday.format(DATE_FORMAT)];
}

/** 跨度是否超过 31 天（含首尾） */
export function isRangeTooLong(startDate: string, endDate: string): boolean {
    return dayjs(endDate).diff(dayjs(startDate), 'day') >= MAX_RANGE_DAYS;
}

export interface RiskLogDateGroup {
    date: string;
    /** enum.weekdaysOptions 下的 key */
    weekdayKey: (typeof WEEKDAY_KEYS)[number];
    items: DriverActivityRiskLogItem[];
}

/** 当前页按变化时刻倒序（后端已倒序，这里兜底）后按美西日期分组 */
export function groupByLaDate(items: DriverActivityRiskLogItem[]): RiskLogDateGroup[] {
    const groups: RiskLogDateGroup[] = [];
    [...items]
        .sort((a, b) => Number(b.changedAt) - Number(a.changedAt))
        .forEach((item) => {
            const day = dayjs(Number(item.changedAt)).tz(ACTIVITY_TIME_ZONE);
            const date = day.format(DATE_FORMAT);
            const last = groups[groups.length - 1];
            if (last?.date === date) {
                last.items.push(item);
            } else {
                groups.push({ date, weekdayKey: WEEKDAY_KEYS[day.day()], items: [item] });
            }
        });
    return groups;
}

export function formatLaTime(epochMs: string): string {
    return dayjs(Number(epochMs)).tz(ACTIVITY_TIME_ZONE).format('HH:mm:ss');
}

const orEmpty = (value?: string | number | null): string =>
    value === null || value === undefined || String(value).trim() === '' ? EMPTY_MARK : String(value);

/** 一条日志的文案 i18n key + 参数；未知类型返回 null，由页面原样展示 changeType */
export function buildRiskLogText(item: DriverActivityRiskLogItem): { key: string; params: Record<string, string> } | null {
    if (item.changeType === RISK_LOG_CHANGE_TYPE.DEVICE_CHANGED) {
        return {
            key: 'driverOps.riskLog.text.deviceChanged',
            params: {
                accountId: orEmpty(item.accountId),
                previousDeviceId: orEmpty(item.previousDeviceId),
                currentDeviceId: orEmpty(item.currentDeviceId),
            },
        };
    }
    if (item.changeType === RISK_LOG_CHANGE_TYPE.ACCOUNT_CHANGED) {
        return {
            key: 'driverOps.riskLog.text.accountChanged',
            params: {
                // 与后端 Excel 一致：同一设备，取变化后设备ID，缺失时退回变化前
                deviceId: orEmpty(item.currentDeviceId ?? item.previousDeviceId),
                previousAccountId: orEmpty(item.previousAccountId),
                currentAccountId: orEmpty(item.currentAccountId),
            },
        };
    }
    return null;
}

/** 日期 → 命中规则展示串（如 "R1、R3"）；无命中的日期不在 Map 中 */
export function buildDailyHitMap(dailyHits?: DriverActivityRiskDailyHit[] | null): Map<string, string> {
    return new Map((dailyHits ?? []).filter((hit) => hit.hitRules.length > 0).map((hit) => [hit.date, hit.hitRules.join('、')]));
}
